const API_BASE = import.meta.env.VITE_API_URL || '';

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let getAccessToken: () => string | null = () => null;
let refreshTokens: () => Promise<boolean> = async () => false;
let onAuthFailed: () => void = () => {};

export function configureApiAuth(config: {
  getToken: () => string | null;
  refresh: () => Promise<boolean>;
  onFailed: () => void;
}) {
  getAccessToken = config.getToken;
  refreshTokens = config.refresh;
  onAuthFailed = config.onFailed;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Auto-refresh on 401
  if (response.status === 401 && token) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      const newToken = getAccessToken();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        response = await fetch(`${API_BASE}${path}`, { ...options, headers });
      }
    } else {
      onAuthFailed();
      throw new ApiError('UNAUTHORIZED', 'Session expired', 401);
    }
  }

  if (!response.ok) {
    let errorBody: any;
    try {
      errorBody = await response.json();
    } catch {
      throw new ApiError('INTERNAL_ERROR', response.statusText, response.status);
    }
    throw new ApiError(
      errorBody?.error?.code || 'UNKNOWN',
      errorBody?.error?.message || 'Request failed',
      response.status,
      errorBody?.error?.details
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
};

export { ApiError };
